import ko from 'knockout';
import _ from 'underscore';
import { DeterministicViewForBackBoneView } from '@bingads-webui/deterministic-wrapper-view';
import { PureKnockoutView } from '@bingads-webui/knockout-view';
import { PredicateList } from '@bingads-webui/predicate-list';
import { parseSync } from '@bingads-webui/safe-json';
import { withUiBlock } from '@bingads-webui/uiblocker';
import { Router } from '@bingads-webui/router';
import { AlertHelper } from '@bingads-webui-campaign/alert-helper';
import { PreviewResultView } from '@bingads-webui-campaign/bulk-operation-base';
import { localize, convertFilterEntityToPredicates } from '@bingads-webui/filter-commons';
import { SchemaI18n } from '@bingads-webui-universal/schema-i18n';

import { RuleCronSelectionview } from './cron-selection/view';
import { RuleAuthoringViewModel } from './viewmodel';
import template from './template.pug';
import { refresh } from './grid-helper';
import { getEntitySetting } from './entity-settings/get-entity-setting';
import { RuleAuthoringHelper } from './helper';
import { scopeOptions } from './options';
import { PredicateListBackboneView } from './predicate-list-backbone-view';

export class RuleAuthoringView extends PureKnockoutView {
  /**
   * Initialize automated rules settings
   * @param {Object} param - An object containing these default options for model
   * @param {Object} param.grid - the new grid object
   * @param {Object} param.i18n - localization object
   * @param {Object} param.instrumentation - instrumentation object
   * @param {Object} param.permissions - permissions for the user
   * @param {String} param.accountTimeZone - accountTimeZone
   * @param {String} param.accountCurrencyCode - accountCurrencyCode
   * @param {Object} param.stateMgr - the state manager which contains {getItem, setItem}
   * @param {Object} param.preferenceSvc - preference service to store user preferences
   * @param {Object} param.appCfg - global app config
   * @param {Object} param.bidBudgetRange - {FloorValue, CeilingValue} for selected rule
   * @param {String} param.currentUserId - currentUserId
   *
   * @returns {void} void
   */
  initialize({
    koModel,
    grid,
    i18n,
    instrumentation,
    permissions,
    accountTimeZone,
    accountCurrencyCode,
    stateMgr,
    preferenceSvc,
    appCfg,
    bidBudgetRange,
    currentUserId,
    isForVNext = false,
    scope,
    newI18n,
    CampaignAdgroupSelectorEdmView,
    currentEntity,
    PredicateListReact,
    getPredicateIconClassName,
  }) {
    this.model = koModel;
    this.isForVNext = isForVNext;
    this.currentEntity = currentEntity;
    this.CampaignAdgroupSelectorEdmView = CampaignAdgroupSelectorEdmView;
    this.appCfg = appCfg;
    const selectedRule = koModel.get('SelectedRule');
    const { entity, action } = selectedRule;

    if (selectedRule.id != null && (entity === 'Campaign' || entity === 'AdGroup')) {
      scopeOptions.SelectCampaignOrAdGroup = 3;
    } else {
      delete scopeOptions.SelectCampaignOrAdGroup;
    }

    this.entitySetting = getEntitySetting(
      entity,
      i18n,
      selectedRule,
      accountCurrencyCode,
      bidBudgetRange
    );
    this.options = {
      i18n,
      grid,
      instrumentation,
      entity,
      menuAction: action,
      selectedRule,
      currentUserId,
      isActionText: this.entitySetting.isActionText(),
    };

    super.initialize(this.options);

    this.koModel = koModel;
    this.i18n = i18n;
    this.newI18n = newI18n;
    this.scope = scope;
    this.helper = new RuleAuthoringHelper({ i18n, permissions });
    this.alertHelper = new AlertHelper({ i18n });
    this.initializeInstrumentation();
    this.viewModel.selectedScopeOption.subscribe(() => {
      this.onScopeSelectionChange();
    }, this);

    this.once('rendered', () => {
      // set cron selection view
      this.cronselectionView = new RuleCronSelectionview({
        i18n,
        entity,
        selectedRule,
        permissions,
        accountTimeZone,
        stateMgr,
        preferenceSvc,
        appCfg,
        isEmailOption: this.viewModel.isEmailOption(),
        instrumentation,
        isForVNext,
      });
      this.setChild('cron-selection', this.cronselectionView);

      // set action selected view (for specific rule types)
      const actionView = this.entitySetting.getActionView();
      if (actionView) {
        this.setChild('action', actionView);
      }

      if (isForVNext) {
        this.options.grid.pgridPlugins.filterService.entitySchema = localize(
          this.options.grid.pgridPlugins.filterService.entitySchema,
          new SchemaI18n({ i18n }),
          i18n
        );

        // update schema for editorial reasons for ads/keywords
        const { entitySchema } = this.options.grid.pgridPlugins.filterService;
        this.predicateListView = new PredicateListBackboneView({
          PredicateList: PredicateListReact,
          entitySchema,
          i18n: newI18n,
          readOnlyProperties: ['EditorialRejectionFlagId', 'EditorialRejectionDate', 'IsAppealPending', 'EditorialStatus', 'HeadlinesAssetId', 'DescriptionsAssetId', 'SeasonalityAdjustmentId', 'DataExclusionId'],
          editableFilter: this.options.grid.pgridPlugins.filterService.getEditableFilter(),
          getPredicateIconClassName,
          onEditableFilterUpdate: _.bind((query) => {
            this.predicateListView.editableFilter =
              this.options.grid.pgridPlugins.filterService.createFilterEntity(query);
            this.predicateListView.render();
          }, this),
        });
      } else {
        this.predicateListView = new PredicateList(_.extend({
          entitySchema: this.options.grid.pgridPlugins.filterService.entitySchema,
          selected: this.options.grid.pgridPlugins.filterService.getEditableFilter(),
          isComparingEnabled: false,
          i18n,
        }, this.options.grid.pgridPlugins.filterService.filterEntityOptions));
      }

      const predicateListViewWrapper = new DeterministicViewForBackBoneView({
        view:
        this.predicateListView,
      });
      this.setChild('filter', predicateListViewWrapper);

      const view = new PreviewResultView({
        columns: this.entitySetting.getPreviewHeaders(),
        i18n,
        results: [],
        totalCount: 0,
      });
      this.setChild('preview-result', view);
    });
  }

  initializeInstrumentation() {
    const scenario = this.options.instrumentation[`authorRule${this.viewModel.entity}`].create();

    scenario.createRule.create().monitor('saveRule', this);
    scenario.previewRuleResults.create().monitor('previewRule', this);
  }

  get events() {
    return {
      'click .btn-ruleAuth-save': 'saveRule',
      'click .btn-ruleAuth-preview': 'previewRule',
      'click .btn-ruleAuth-cancel': 'hideView',
    };
  }

  get template() {
    return template;
  }

  get ViewModel() {
    return RuleAuthoringViewModel;
  }

  setupCampaignAdGroupSelector(value) {
    this.selectorView = new this.CampaignAdgroupSelectorEdmView({
      el: this.$('.campaignAdgroupSelector'),
      i18n: this.newI18n,
      scope: this.scope,
      isCampaignMode: this.viewModel.showCampaigns,
      value,
    });
    this.selectorView.render();
    this.selectorView.on('on-apply', (selectedItems) => {
      const info = this.viewModel.selectedScopeInfo;

      info.selectedRowCount = selectedItems.length;
      info.selectedRowIds = _.map(selectedItems, item => JSON.parse(item).Id);

      this.updateSelectedEntityCount(info);
      this.viewModel.selectedScopeOption(scopeOptions.Selected);
    });
  }

  onScopeSelectionChange() {
    if (this.viewModel.isEditRule
      && (this.viewModel.showCampaigns || this.viewModel.showAdGroups)
      && this.viewModel.selectedScopeOption() === scopeOptions.SelectCampaignOrAdGroup) {
      this.viewModel.showCampaignAdGroupSelector(true);

      if (!_.isEmpty(this.viewModel.selectedScopeInfo.selectedRowIds)
        && this.viewModel.selectedScopeInfo.selectedRowIds.length < 100) {
        const filterString = _.reduce(
          this.viewModel.selectedScopeInfo.selectedRowIds,
          (filter, id, index, list) => filter.concat(`Id eq ${id}${(index === list.length - 1) ? '' : ' or '}`), ''
        );
        const uiblocker = withUiBlock();
        if (this.viewModel.showCampaigns) {
          this.currentEntity.Campaigns.get({
            select: ['Id', 'Name', 'Status'],
            filter: {
              where: filterString,
            },
            limit: 1000,
          }).fetch().then((data) => {
            const entitySelection = _.map(data.value, item => ({
              Id: item.Id,
              name: item.Name,
              status: item.Status,
            }));
            this.setupCampaignAdGroupSelector(entitySelection);
            uiblocker();
          });
        } else {
          this.currentEntity.AdGroups.get({
            filter: {
              where: filterString,
            },
            limit: 1000,
            select: ['Id', 'Name', 'Status', 'Campaign'],
          }).fetch().then((data) => {
            const entitySelection = _.map(data.value, item => ({
              Id: item.Id,
              name: item.Name,
              status: item.Status,
              campaignName: item.Campaign ? item.Campaign.Name : null,
              CampaignId: item.Campaign ? item.Campaign.Id : null,
            }));
            this.setupCampaignAdGroupSelector(entitySelection);
            uiblocker();
          });
        }
      } else {
        this.setupCampaignAdGroupSelector();
      }
    } else {
      this.viewModel.showCampaignAdGroupSelector(false);
    }
  }

  // set actions based on the view model value
  setAction() {
    return this.koModel.set('BatchAction', this.entitySetting.getAction(this.viewModel));
  }

  onSaveOrPreviewRuleError(xhr, response, uiblocker) {
    uiblocker();
    const errors = _.isArray(response) ? response : _.result(parseSync(response.responseText), 'value');
    const isUnauthorized = _.find(errors, item => item.Code === 'TokenExpired');

    if (isUnauthorized) {
      return Router.navigate('/default.aspx?reason=1&msg=sessionexpired');
    }

    const isScheduleInvalid = _.find(errors, item => item.Code === 'TaskSchedulingInvalid');
    const { i18n } = this.options;
    if (isScheduleInvalid) {
      const scheduleInvalidMsg = i18n.getString('Validate_ScheduleTimeInvalid');
      this.cronselectionView.viewModel.frequencyErrorMessage(scheduleInvalidMsg);
      return; // eslint-disable-line consistent-return
    }

    const isThrottlingError = _.find(errors, item => item.Code === 'ThrottlingLimitReached');

    if (isThrottlingError) {
      const throttLimitMsg = i18n.getString('Error_Campaign_API_ThrottlingLimitReached');
      return this.alertHelper.showError(throttLimitMsg);
    }

    const isCustomerMigrationError = _.find(errors, item => item.Code === 'CustomerUnderMigration');

    if (isCustomerMigrationError) {
      const migrationMsg = i18n.getString('Error_Campaign_API_CustomerUnderMigration');
      return this.alertHelper.showError(migrationMsg);
    }

    return this.alertHelper.showError(i18n.getString('GenericErrorMessage'));
  }

  saveRule() {
    if (!this.validate() || !this.cronselectionView.validate()) {
      return;
    }

    if (this.viewModel.isEditRule) {
      const ruleUrlTmpl = _.template('Advertiser/Customers()/Accounts()/TaskItems({{ruleId}})?');
      this.koModel.url = ruleUrlTmpl({
        ruleId: this.viewModel.editRuleId,
      });
    } else {
      this.koModel.url = 'Advertiser/Customers()/Accounts()/TaskItems?';
    }
    this.koModel.isEditRule = this.viewModel.isEditRule;
    this.koModel.isSimulation = false;
    this.koModel.set({
      Entity: this.viewModel.entity,
      Name: this.viewModel.ruleName(),
      NotificationType: this.viewModel.selectedEmailOption(),
      TimeZoneId: this.cronselectionView.viewModel.selectedTimeZoneOption(),
    });
    this.setAction();
    this.koModel.set('Cron', this.cronselectionView.getCron());
    const currentCustomerId = this.appCfg && this.appCfg.get && this.appCfg.get('CurrentCustomer') ? this.appCfg.get('CurrentCustomer').Id : undefined;
    if (currentCustomerId) {
      this.koModel.set('CurrentCustomerId', currentCustomerId);
    }
    this.helper.setSelectionAndDescription(
      this,
      this.model,
      this.options.grid,
      false,
      this.options.i18n
    );

    const uiblocker = withUiBlock();
    this.koModel.save({}, {
      success: () => {
        this.hideView();

        if (this.viewModel.isEditRule) {
          refresh(this.options.grid);
        }

        this.alertHelper.showSuccess(this.options.i18n.getString('RuleAuthoring_StatusMessage'));
        uiblocker();
      },
      error: (xhr, errors) => {
        this.onSaveOrPreviewRuleError(xhr, errors, uiblocker);
      },
    });
  }

  previewRule() {
    if (!this.validate()) {
      return;
    }

    this.koModel.url = 'Advertiser/Customers()/Accounts()/BulkEditSessions?$expand=BulkEditResults&preview=true';
    this.koModel.isSimulation = true;
    this.koModel.set('Entity', this.viewModel.entity);
    this.koModel.unset('PreviewEntityIds');

    if (this.options.grid.pgridPlugins.crossPageSelectAll.isAllSelected) {
      this.koModel.set('PreviewEntityIds', _.map(this.options.grid.gridView.selectedKeys(), Number));
    }

    const currentCustomerId = this.appCfg && this.appCfg.get && this.appCfg.get('CurrentCustomer') ? this.appCfg.get('CurrentCustomer').Id : undefined;
    if (currentCustomerId) {
      this.koModel.set('CurrentCustomerId', currentCustomerId);
    }

    this.setAction();
    this.helper.setSelectionAndDescription(
      this,
      this.model,
      this.options.grid,
      true,
      this.options.i18n
    );

    const uiblocker = withUiBlock();
    this.koModel.save({}, {
      success: (model) => {
        uiblocker();
        const results = model.get('BulkEditResults');

        // create or update preview view
        this.getChild('preview-result').update(
          this.entitySetting.formatPreviewResults(results),
          model.get('TotalRowCount')
        );
        this.viewModel.inPreviewMode(true);
      },
      error: (xhr, errors) => {
        this.onSaveOrPreviewRuleError(xhr, errors, uiblocker);
      },
    });
  }

  hideView() {
    this.trigger('hide');
    ko.cleanNode(this.el);
    if (this.selectorView) {
      this.selectorView.remove();
    }
    this.remove();
    return this;
  }

  validate() {
    let isResultValid = true;

    if (_.isFunction(this.viewModel.validate)) {
      isResultValid = this.viewModel.validate();
    }

    isResultValid = isResultValid && this.entitySetting.isActionValidate();

    if (!this.isForVNext) {
      // user can create automated rules without any when condition
      if (this.predicateListView.ko_model.items().length > 0) {
        _.each(this.predicateListView.ko_model.items(), (filterItem) => {
          filterItem.onValueChange(); // trigger value checking so that error message can be shown
        });
        isResultValid = isResultValid && this.predicateListView.ko_model.canApply();
      }
    } else {
      const editablePredicates = convertFilterEntityToPredicates(this.predicateListView.editableFilter); // eslint-disable-line max-len
      const hasInvalidProperties = editablePredicates.some(editablePredicate =>
        editablePredicate.isDeletedProperty || editablePredicate.isInvalidProperty);
      isResultValid = isResultValid && !hasInvalidProperties;
    }

    if (!isResultValid) {
      this.$el.find('input.error:visible').first().focus();
    }

    return isResultValid;
  }

  /**
   * update selected entity count
   * @param {Object} info - An object containing the new selection info
   *
   * @returns {void} void
   */
  updateSelectedEntityCount(info) {
    return this.viewModel.updateSelectedEntityCount(info);
  }

  addWalkme({
    directive,
    walkMe,
    walkMeLink,
    walkThrus,
  }) {
    const selectedRule = this.model.get('SelectedRule');

    // right now we only want to show this for change bid rule
    if (selectedRule && selectedRule.action === 'ChangeBid') {
      const Walkme = walkMe.default;
      const WalkmeLink = walkMeLink.default;

      directive.whenLoaded().then(() => {
        const walkme = new Walkme();

        walkme.setType(WalkmeLink)
          .setWalkthrus(walkThrus.keyword.link.changeBid)
          .render();

        walkme.$el.addClass('walkme__link--panel-title');
        this.$el.find('.panel-heading').append(walkme.$el);
      });
    }
  }
}
